/**
 * Customer quote policy — admin API client
 * Paths: /quotes/policy (settings), /quotes/:quoteId/policy/* (per-quote actions)
 */

import api from "@/shared/api.jsx";
import { logError, logInfo } from "@/shared/utils/logger.js";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

function unwrapData(response) {
  if (response?.data !== undefined) return response.data;
  return response;
}

/**
 * GET /quotes/policy — current published policy
 * @returns {Promise<{ enabled: boolean, version: string, title: string, html: string }>}
 */
export async function getQuotePolicy() {
  try {
    const response = await api.get("/quotes/policy");
    logInfo("[QuotePolicyService] Loaded policy");
    return unwrapData(response);
  } catch (error) {
    logError("[QuotePolicyService] Failed to load policy", {
      error: error.message,
    });
    throw error;
  }
}

/**
 * PUT /quotes/policy — publish policy
 * @param {{ html: string, title: string, enabled: boolean, bumpVersion?: boolean }} payload
 */
export async function publishQuotePolicy(payload) {
  try {
    const response = await api.put("/quotes/policy", {
      html: payload.html,
      title: payload.title,
      enabled: Boolean(payload.enabled),
      bumpVersion: payload.bumpVersion !== false,
    });
    logInfo("[QuotePolicyService] Published policy");
    return unwrapData(response);
  } catch (error) {
    logError("[QuotePolicyService] Failed to publish policy", {
      error: error.message,
    });
    throw error;
  }
}

/**
 * POST /quotes/policy/preview.pdf — PDF preview (blob)
 * Note: backend uses POST (optional draft html/title), not GET.
 * @param {{ html?: string, title?: string }} [draft]
 * @returns {Promise<Blob>}
 */
export async function previewQuotePolicyPdf(draft = {}) {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await fetch(`${API_BASE_URL}/quotes/policy/preview.pdf`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/pdf",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        html: draft.html,
        title: draft.title,
      }),
    });

    if (!response.ok) {
      let message = `Preview failed (${response.status})`;
      try {
        const errBody = await response.json();
        message = errBody.error || errBody.message || message;
      } catch {
        // ignore parse errors
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    logInfo("[QuotePolicyService] Preview PDF ready");
    return blob;
  } catch (error) {
    logError("[QuotePolicyService] Failed to preview PDF", {
      error: error.message,
    });
    throw error;
  }
}

/**
 * Open a PDF blob in a new tab (best-effort download fallback).
 * @param {Blob} blob
 * @param {string} [filename]
 */
export function openPdfBlob(blob, filename = "customer-policy-preview.pdf") {
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * POST /quotes/:quoteId/policy/share-link
 * @param {string} quoteId
 * @returns {Promise<{ shareableUrl: string, tokenId: string }>}
 */
export async function createPolicyShareLink(quoteId) {
  try {
    const response = await api.post(
      `/quotes/${encodeURIComponent(quoteId)}/policy/share-link`,
      {}
    );
    logInfo("[QuotePolicyService] Created policy share link", { quoteId });
    return unwrapData(response);
  } catch (error) {
    logError("[QuotePolicyService] Failed to create share link", {
      quoteId,
      error: error.message,
    });
    throw error;
  }
}

/**
 * POST /quotes/:quoteId/policy/email
 * @param {string} quoteId
 */
export async function emailPolicyAgreement(quoteId) {
  try {
    const response = await api.post(
      `/quotes/${encodeURIComponent(quoteId)}/policy/email`,
      {}
    );
    logInfo("[QuotePolicyService] Emailed policy agreement", { quoteId });
    return unwrapData(response);
  } catch (error) {
    logError("[QuotePolicyService] Failed to email policy agreement", {
      quoteId,
      error: error.message,
    });
    throw error;
  }
}

/**
 * GET /quotes/:quoteId/policy — per-quote agreement status
 * @param {string} quoteId
 */
export async function getQuotePolicyStatus(quoteId) {
  try {
    const response = await api.get(
      `/quotes/${encodeURIComponent(quoteId)}/policy`
    );
    return unwrapData(response);
  } catch (error) {
    logError("[QuotePolicyService] Failed to get quote policy status", {
      quoteId,
      error: error.message,
    });
    throw error;
  }
}
